/* eslint-disable no-unused-vars */
// src/components/AdvancedSubCRUD.js

import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import {
  readRecords,
  saveRecord,
  updateRecord,
  deleteRecord
} from "../services/firestoreStorage";

/**
 * AdvancedSubCRUD – حسابات محاسبية مضبوطة
 */
export default function AdvancedSubCRUD({
  branchId,
  dataKey,
  title,
  fields = [],
  numericFields = [],
  enableAttendance = false
}) {
  const hasNameField = fields.some(f =>
    f.name === "name" || /name|اسم/i.test(f.name + "|" + (f.label || ""))
  );

  const effectiveFields = useMemo(() => {
    if (hasNameField) return fields;
    return [{ name: "name", label: "الاسم", type: "text" }, ...fields];
  }, [fields, hasNameField]);

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);

  // filters
  const [q, setQ] = useState("");
  const [nameQ, setNameQ] = useState("");
  const [dateQ, setDateQ] = useState("");

  async function loadData() {
    const data = await readRecords(branchId, dataKey);
    setRows(data);
  }

  useEffect(() => {
    const init = {};
    effectiveFields.forEach(f => (init[f.name] = ""));
    setForm(init);
    loadData();
  }, [branchId, dataKey, effectiveFields]);

  async function handleAddOrUpdate() {
    const payload = {};

    effectiveFields.forEach(f => {
      let val = form[f.name];
      if (f.type === "number") val = val === "" ? 0 : Number(val);
      payload[f.name] = val;
    });

    if (editingId) {
      await updateRecord(branchId, dataKey, editingId, payload);
      setEditingId(null);
    } else {
      await saveRecord(branchId, dataKey, payload);
    }

    const cleared = {};
    effectiveFields.forEach(f => (cleared[f.name] = ""));
    setForm(cleared);

    loadData();
  }

  function handleEdit(item) {
    setEditingId(item.id);
    const newForm = {};
    effectiveFields.forEach(f => {
      newForm[f.name] = item[f.name] ?? "";
    });
    setForm(newForm);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleDelete(id) {
    if (!window.confirm("هل أنت متأكد من الحذف؟")) return;
    await deleteRecord(branchId, dataKey, id);
    loadData();
  }

  /* =========================
     🔵 الحضور / الغياب
     ========================= */
  async function markAttendance(id, status) {
    await updateRecord(branchId, dataKey, id, {
      attendance: status
    });
    loadData();
  }

const filteredRows = rows
  .filter(r => {
    if (q && !JSON.stringify(r).toLowerCase().includes(q.toLowerCase()))
      return false;

    if (nameQ) {
      const v = String(r.name || "").toLowerCase();
      if (!v.includes(nameQ.toLowerCase())) return false;
    }

    if (dateQ) {
      let recordDate = "";
      if (typeof r.date === "string") recordDate = r.date;
      else if (r.createdAt?.toDate)
        recordDate = r.createdAt.toDate().toISOString().slice(0, 10);
      if (!recordDate || recordDate !== dateQ) return false;
    }

    return true;
  })
  .sort((a, b) => {
    // ترتيب من الأحدث للأقدم
    const getDate = r => {
      if (r.date) return new Date(r.date);
      if (r.createdAt?.toDate) return r.createdAt.toDate();
      return new Date(0);
    };
    return getDate(b) - getDate(a);
  });

  /* =========================
     🧮 إجمالي السطر
     ========================= */
const getRowTotal = row => {
  const getVal = keywords => {
    const field = effectiveFields.find(f =>
      keywords.some(k =>
        (f.name + " " + (f.label || "")).toLowerCase().includes(k)
      )
    );
    return field ? Number(row[field.name] || 0) : 0;
  };

  // ===== القيم العامة =====
  const fixedAfter = getVal(["بعد", "after"]);
  const cashHome = getVal(["نقدي", "cash"]);
  const bless = getVal(["كرم"]);
  const withdraw = getVal(["سحب", "withdraw"]);
  const insurance = getVal(["تأمين", "insurance"]);

  // ===== حسابات التجار =====
  const invoice = getVal(["فاتورة", "invoice"]);
  const payment = getVal(["دفعة", "payment"]);

  // ===== المبيعات (كل الفروع) =====
  const rent = getVal(["ايجار", "إيجار", "rent"]);
  const expenses = getVal(["مصاريف", "expense"]);
  const extra = getVal(["خوارج", "extra"]);
  const sold = getVal(["مباع", "sold"]);

  // 1️⃣ الحسابات الرئيسية
  if (fixedAfter !== 0) {
    return fixedAfter + cashHome - withdraw - insurance;
  }

  // 2️⃣ حسابات التجار
  if (invoice !== 0 || payment !== 0) {
    return invoice - payment   ;
  }

  // 3️⃣ المبيعات (كل الفروع)
  if (rent !== 0 || expenses !== 0 || extra !== 0 || sold !== 0) {
    return sold - (rent + expenses + extra)  ;
  }

  // 4️⃣ باقي الصفحات
  return cashHome + bless - withdraw;
};

  const tableTotal = useMemo(() => {
    return filteredRows.reduce((sum, r) => sum + getRowTotal(r), 0);
  }, [filteredRows]);

  const columnTotals = useMemo(() => {
    const totals = {};

    effectiveFields.forEach(f => {
      if (f.type !== "number") {
        totals[f.name] = "";
        return;
      }

      totals[f.name] = filteredRows.reduce((sum, r) => {
        return sum + Number(r[f.name] || 0);
      }, 0);
    });

    return totals;
  }, [filteredRows, effectiveFields]);

  const attendanceTotals = useMemo(() => {
    let present = 0;
    let absent = 0;

    filteredRows.forEach(r => {
      if (r.attendance === "present") present++;
      if (r.attendance === "absent") absent++;
    });

    return { present, absent };
  }, [filteredRows]);

  return (
    <div style={{ width: "100%" }}>
      <h3>{title}</h3>

      {/* Filters */}
      <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input className="input" placeholder="بحث عام..." value={q} onChange={e => setQ(e.target.value)} />
        <input className="input" placeholder="بحث بالاسم..." value={nameQ} onChange={e => setNameQ(e.target.value)} />
        <input className="input" type="date" value={dateQ} onChange={e => setDateQ(e.target.value)} />
        <button className="btn ghost" onClick={loadData}>تحديث</button>
      </div>

      {/* Form */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {effectiveFields.map(f => (
            <input
              key={f.name}
              className="input"
              placeholder={f.label}
              type={f.type}
              value={form[f.name]}
              onChange={e => setForm({ ...form, [f.name]: e.target.value })}
            />
          ))}
          <button className="btn" onClick={handleAddOrUpdate}>
            {editingId ? "حفظ" : "إضافة"}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {effectiveFields.map(f => (
                <th key={f.name} style={{ padding: 8, textAlign: "right" }}>
                  {f.label}
                </th>
              ))}
              <th style={{ textAlign: "right" }}>إجمالي السطر</th>

              {enableAttendance && (
                <>
                  <th style={{ textAlign: "center" }}>حاضر</th>
                  <th style={{ textAlign: "center" }}>غائب</th>
                </>
              )}

              <th style={{ textAlign: "center" }}>إجراءات</th>
            </tr>
          </thead>

<tbody>
  {/* ===== صف الإجماليات (أعلى الجدول) ===== */}
  <tr style={{ background: "rgba(0,0,0,0.08)", fontWeight: "bold" }}>
    {effectiveFields.map(f => (
      <td key={f.name} style={{ padding: 8 }}>
        {columnTotals[f.name] !== "" ? columnTotals[f.name] : "—"}
      </td>
    ))}

    <td style={{ padding: 8 }}>{tableTotal}</td>

    {enableAttendance && <td colSpan={2} style={{ textAlign: "center" }}>—</td>}

    <td style={{ textAlign: "center" }}>الإجمالي</td>
  </tr>

  {/* ===== صفوف البيانات ===== */}
  {filteredRows.map(r => (
    <tr key={r.id}>
      {effectiveFields.map(f => (
        <td key={f.name} style={{ padding: 8 }}>
          {r[f.name] ?? "-"}
        </td>
      ))}

      <td style={{ padding: 8, fontWeight: "bold" }}>
        {getRowTotal(r)}
      </td>

      {enableAttendance && (
        <>
          <td style={{ textAlign: "center" }}>
            <input
              type="checkbox"
              checked={r.attendance === "present"}
              onChange={() => markAttendance(r.id, "present")}
            />
            {r.attendance === "present" && (
              <span style={{ color: "#22c55e", marginInlineStart: 6 }}>✔</span>
            )}
          </td>

          <td style={{ textAlign: "center" }}>
            <input
              type="checkbox"
              checked={r.attendance === "absent"}
              onChange={() => markAttendance(r.id, "absent")}
            />
            {r.attendance === "absent" && (
              <span style={{ color: "#ef4444", marginInlineStart: 6 }}>✖</span>
            )}
          </td>
        </>
      )}

      <td style={{ textAlign: "center" }}>
        <button className="btn ghost" onClick={() => handleEdit(r)}>
          تعديل
        </button>
        <button
          className="btn"
          style={{ background: "#dc2626" }}
          onClick={() => handleDelete(r.id)}
        >
          حذف
        </button>
      </td>
    </tr>
  ))}
</tbody>
        </table>

        <div style={{ marginTop: 14, padding: 12, fontWeight: "bold", textAlign: "left" }}>
          إجمالي الجدول: {tableTotal}
        </div>

        {enableAttendance && (
          <div style={{ padding: 12, fontWeight: "bold" }}>
            ✔️ الحضور: {attendanceTotals.present} | ❌ الغياب: {attendanceTotals.absent}
          </div>
        )}
      </div>
    </div>
  );
}

AdvancedSubCRUD.propTypes = {
  branchId: PropTypes.string.isRequired,
  dataKey: PropTypes.string.isRequired,
  title: PropTypes.string,
  fields: PropTypes.array,
  numericFields: PropTypes.array,
  enableAttendance: PropTypes.bool
};
